import axios from "axios";
import {getApiBaseUrl} from "./api";


const API_URL = getApiBaseUrl();


// Create axios instance with default config
const adminApiClient = axios.create({
  baseURL: API_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

console.log("🔧 API Service - Created adminAPI instance");

// Add request interceptor to attach auth token
adminApiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");
    console.log("Inserting token into request", token);
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor for error handling
adminApiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid - redirect to login
      localStorage.removeItem("token");
      window.location.href = "/portal/login";
    } else if (error.response?.status === 413) {
      error.userMessage =
        "File size is too large. Please upload a smaller file.";
    } else if (error.response?.status === 500) {

      error.userMessage = error.response?.data?.error;
    }
    return Promise.reject(error);
  }
);

// API methods
// Admin API methods
const adminApi = {
  // Create new category
  createCategory: async (categoryData) => {
    const response = await adminApiClient.post("/categories", categoryData);
    return response.data;
  },

  // Update category
  updateCategory: async (id, categoryData) => {
    const response = await adminApiClient.put(
      `/categories/${id}`,
      categoryData
    );
    return response.data;
  },

  // Delete category
  deleteCategory: async (id) => {
    const response = await adminApiClient.delete(`/categories/${id}`);
    return response.data;
  },

  // Create new art tag
  createArtTag: async (artTagData) => {
    const response = await adminApiClient.post(`/arttags`, artTagData);
    return response.data;
  },

  // Update art tag
  updateArtTag: async (categoryId, artTagId, artTagData) => {
    const response = await adminApiClient.put(
      `/arttags/${artTagId}`,
      artTagData
    );
    return response.data;
  },

  // Delete art tag
  deleteArtTag: async (artTagId) => {
    const response = await adminApiClient.delete(`/arttags/${artTagId}`);
    return response.data;
  },

  // Create new artwork
  createArtwork: async (artworkData) => {
    const response = await adminApiClient.post("/artworks", artworkData);
    return response.data;
  },

  // Update artwork
  updateArtwork: async (artworkId, artworkData) => {
    const response = await adminApiClient.put(
      `/artworks/${artworkId}`,
      artworkData
    );
    return response.data;
  },

  // Delete artwork (completely)
  deleteArtwork: async (artworkId) => {
    const response = await adminApiClient.delete(`/artworks/${artworkId}`);
    return response.data;
  },

  // Add artwork to tag (keeps existing tags)
  addArtworkToTag: async (tagId, artworkId) => {
    const response = await adminApiClient.post(
      `/artworks/${artworkId}/arttags/${tagId}`
    );
    return response.data;
  },

  // Remove artwork from tag (doesn't delete artwork)
  removeArtworkFromTag: async (tagId, artworkId) => {
    const response = await adminApiClient.delete(
      `/artworks/${artworkId}/arttags/${tagId}`
    );
    return response.data;
  },

  // Add artwork to tag (keeps existing tags)
  linkArtworkToArtist: async (artworkId, artistId) => {
    const response = await adminApiClient.post(
      `/artworks/${artworkId}/artist/${artistId}`
    );
    return response.data;
  },

  // Remove artwork from tag (doesn't delete artwork)
  unlinkArtworkFromArtist: async (artworkId, artistId) => {
    const response = await adminApiClient.delete(
      `/artworks/${artworkId}/artist/${artistId}`
    );
    return response.data;
  },

  getMediums: async () => {
    const response = await adminApiClient.get("/mediums");
    return response.data;
  },

  // Create new medium
  createMedium: async (mediumData) => {
    const response = await adminApiClient.post("/mediums", mediumData);
    return response.data;
  },

  // Update medium
  updateMedium: async (mediumId, mediumData) => {
    const response = await adminApiClient.put(
      `/mediums/${mediumId}`,
      mediumData
    );
    return response.data;
  },

  // Delete medium
  deleteMedium: async (mediumId) => {
    const response = await adminApiClient.delete(`/mediums/${mediumId}`);
    return response.data;
  },

  // Create artist
  createArtist: async (artistData) => {
    const response = await adminApiClient.post("/artists", artistData);
    return response.data;
  },

  updateArtist: async (artistId, artistData) => {
    const response = await adminApiClient.put(
      `/artists/${artistId}`,
      artistData
    );
    return response.data;
  },

  // Delete artist
  deleteArtist: async (artistId) => {
    const response = await adminApiClient.delete(`/artists/${artistId}`);
    return response.data;
  },


  /**
   * Get images with optional pagination and filtering
   * @param {Object} options - Query options
   * @param {number} options.page - Page number (default: 1)
   * @param {number} options.limit - Items per page (default: 10)
   * @param {string} options.filter - Filter type: 'categories', 'tags', 'artworks', 'unused', 'all'
   * @param {string} options.search - Search term
   * @returns {Promise<Object>} - { images, pagination, filter }
   */
  async getImages(options = {}) {
    const { page = 1, limit = 10, filter = "artworks", search = "" } = options;

    const params = {
      page,
      limit,
      filter,
    };

    if (search) {
      params.search = search;
    }

    const response = await adminApiClient.get("/images", { params });

    return response.data;
  },

  /**
   * Get filter counts (for displaying counts in filter tabs)
   * This could be a separate endpoint or calculated client-side
   */
  async getImageCounts() {
    try {
      console.log('Fetching image counts...');

      // Make parallel requests to get counts for each filter
      const [categories, tags, artworks, unused] = await Promise.all([
        this.getImages({ filter: 'categories', limit: 1, page: 1 }),
        this.getImages({ filter: 'tags', limit: 1, page: 1 }),
        this.getImages({ filter: 'artworks', limit: 1, page: 1 }),
        this.getImages({ filter: 'unused', limit: 1, page: 1 }),
      ]);

      const counts = {
        categories: categories.pagination?.total || 0,
        tags: tags.pagination?.total || 0,
        artworks: artworks.pagination?.total || 0,
        unused: unused.pagination?.total || 0,
      };

      console.log('Image counts:', counts);
      return counts;
    } catch (error) {
      console.error('Error fetching image counts:', error);
      // Return zeros on error to prevent UI from breaking
      return {
        categories: 0,
        tags: 0,
        artworks: 0,
        unused: 0,
      };
    }
  },

  // Upload new image to /images/works
  uploadImage: async (imageFile, imageType) => {
    const formData = new FormData();
    formData.append("image", imageFile);
    formData.append("type", imageType);

    const response = await adminApiClient.post("/images", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    return response.data;
  },

  // Upload category image
  uploadCategoryImage: async (id, imageFile) => {
    const formData = new FormData();
    formData.append("image", imageFile);

    const response = await adminApiClient.post(
      `/categories/${id}/image`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );
    return response.data;
  },

  deleteImage: async (imageUrl) => {
    const response = await adminApiClient.delete("images", {
      data: { imageUrl },
    });
    return response.data;
  },

  // Delete multiple images from /public/images/works
  deleteImages: async (imageUrls) => {
    const response = await adminApiClient.post("/images/delete-multiple", {
      imageUrls,
    });
    return response.data;
  },
};

export default adminApi;
